import Dexie, { type EntityTable } from 'dexie'
import type { StoredEvent } from '../../domain/events.ts'
import type { DeviceId, PhotoId } from '../../domain/ids.ts'

/**
 * Local storage. The event table is append-only by contract: nothing in the app
 * updates or deletes a row in it. Dexie cannot enforce that, so the EventStore is
 * the only module allowed to touch this table.
 */

export interface PhotoRow {
  id: PhotoId
  blob: Blob
  kind: 'reference' | 'result' | 'check'
  bytes: number
  createdAt: string
}

export interface SecretRow {
  /** `pin` is the parent mode. `child:<memberId>` is one child's own screen. */
  key: string
  saltBase64: string
  hashBase64: string
  iterations: number
  consecutiveFailures: number
  lastFailureAt: string | null
  updatedAt: string
}

export interface MetaRow {
  key: 'deviceId' | 'projectionVersion' | 'schemaVersion'
  value: string
}

export class MisionHogarDb extends Dexie {
  events!: EntityTable<StoredEvent, 'id'>
  photos!: EntityTable<PhotoRow, 'id'>
  secrets!: EntityTable<SecretRow, 'key'>
  meta!: EntityTable<MetaRow, 'key'>

  constructor(name = 'mision-hogar') {
    super(name)
    this.version(1).stores({
      events: 'id, seq, [deviceId+seq], day, type, at',
      photos: 'id, kind, createdAt',
      secrets: 'key',
      meta: 'key',
    })
  }
}

export const SCHEMA_VERSION = 1

export async function resolveDeviceId(
  db: MisionHogarDb,
  generate: () => string,
): Promise<DeviceId> {
  const existing = await db.meta.get('deviceId')
  if (existing) return existing.value as DeviceId
  const created = generate()
  await db.meta.put({ key: 'deviceId', value: created })
  return created as DeviceId
}
